import React, { useState, useEffect, useMemo, useCallback } from "react";
import { api } from "../../api";
import { Icon, Spinner, useToast } from "../../components/ui";
import { derive, applyRefData } from "../admin/recordDerive";
import { ProductivityDrill } from "./ProductivityDrill";

const STAGE_KEY = { SUBMISSION: "submissions", END_PROCESS: "endProcess", MODIFICATION: "modifications", MORE_INFO: "moreInfo" };
const blank = () => ({
  submissions: 0, endProcess: 0, modifications: 0, moreInfo: 0, stat: 0,
  approved: 0, paNotReq: 0, denied: 0, pending: 0, canceled: 0, withdraw: 0, reviewed: 0,
  clientIssue: 0, patientIssue: 0, productionIssue: 0,
  deniedL: [], canceledL: [], withdrawL: [], clientL: [], patientL: [], productionL: [],
});

const fmtRev = (n) => { const x = Math.round((Number(n) || 0) * 100) / 100; return x ? String(x) : "—"; };   // fractional Reviewed score (e.g. 9.25)
function mdyToDate(s) { const m = String(s || "").match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); return m ? new Date(+m[3], +m[1] - 1, +m[2]) : null; }
function isoToDate(s) { const m = String(s || "").match(/^(\d{4})-(\d{2})-(\d{2})$/); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null; }

function quickRange(kind, anchor) {
  const t = anchor || (() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()); })();
  switch (kind) {
    case "today": return [t, t];
    case "yesterday": { const y = new Date(t); y.setDate(t.getDate() - 1); return [y, y]; }
    case "thisWeek": { const s = new Date(t); s.setDate(t.getDate() - ((t.getDay() + 6) % 7)); return [s, t]; }
    case "lastWeek": { const s = new Date(t); s.setDate(t.getDate() - ((t.getDay() + 6) % 7) - 7); const e = new Date(s); e.setDate(s.getDate() + 6); return [s, e]; }
    case "thisMonth": return [new Date(t.getFullYear(), t.getMonth(), 1), t];
    case "lastMonth": return [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)];
    case "thisYear": return [new Date(t.getFullYear(), 0, 1), t];
    case "lastYear": return [new Date(t.getFullYear() - 1, 0, 1), new Date(t.getFullYear() - 1, 11, 31)];
    default: return [null, null];
  }
}
const QUICKS = [["today", "Today"], ["yesterday", "Yesterday"], ["thisWeek", "This week"], ["lastWeek", "Last week"], ["thisMonth", "This month"], ["lastMonth", "Last month"], ["thisYear", "This year"], ["lastYear", "Last year"]];

const EXPORT_COLS = [
  ["User", "user"], ["Submissions", "submissions"], ["End process", "endProcess"], ["Modifications", "modifications"], ["More info", "moreInfo"],
  ["STAT", "stat"], ["Approved", "approved"], ["PA N/R", "paNotReq"], ["Denied", "denied"], ["Pending", "pending"], ["Canceled", "canceled"], ["Withdraw", "withdraw"], ["Reviewed", "reviewed"],
  ["Client issue", "clientIssue"], ["Patient issue", "patientIssue"], ["Production issue", "productionIssue"], ["Approval %", "approvalPct"],
];

export function Productivity() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [selUsers, setSelUsers] = useState(new Set());
  const [group, setGroup] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [period, setPeriod] = useState("today");   // default to Today; "" = all time, a quick key, or "custom"
  const [bizToday, setBizToday] = useState(null);   // server business day (resets 08:30 IST), timezone-proof
  useEffect(() => { api.businessToday().then((r) => { const d = isoToDate(r && r.today); if (d) setBizToday(d); }).catch(() => {}); }, []);
  const [usersOpen, setUsersOpen] = useState(false);
  const [drill, setDrill] = useState(null);

  const [reviewActs, setReviewActs] = useState([]);   // fractional Reviewed-effort events {user, day, weight, group}

  const [rFrom, rTo] = useMemo(() => {
    if (period === "custom") return [isoToDate(from), isoToDate(to)];
    if (period) return quickRange(period, bizToday);
    return [null, null];
  }, [period, from, to, bizToday]);

  // Only fetch records worked on/after the start of the selected period (minus a 2-day safety buffer for
  // shift/timezone edges) — so "Today" loads a tiny set instead of every worked record ever. All-time = no cutoff.
  const sinceIso = useMemo(() => {
    if (!rFrom) return "";
    const b = new Date(rFrom); b.setDate(b.getDate() - 2);
    const p2 = (n) => String(n).padStart(2, "0");
    return `${b.getFullYear()}-${p2(b.getMonth() + 1)}-${p2(b.getDate())}`;
  }, [rFrom]);

  const load = useCallback(async () => {
    try {
      const [recs, ref, acts] = await Promise.all([api.workedLight(sinceIso), api.getRefData().catch(() => null), api.reviewActivity().catch(() => [])]);
      applyRefData(ref); setRows(recs); setReviewActs(acts || []);
    } catch (e) { toast(e.message, "err"); setRows([]); }
  }, [toast, sinceIso]);
  useEffect(() => { load(); }, [load]);

  // Live view: silently re-pull the underlying records every 10s so counts stay current on their
  // own. Filters/selection are kept; paused while the tab is hidden or a drill-down is open.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden || drill) return;
      Promise.all([api.workedLight(sinceIso, { silent: true }), api.getRefData({ silent: true }).catch(() => null), api.reviewActivity({ silent: true }).catch(() => [])])
        .then(([recs, ref, acts]) => { applyRefData(ref); setRows(recs); setReviewActs(acts || []); })
        .catch(() => {});
    }, 10000);
    return () => clearInterval(id);
  }, [drill, sinceIso]);

  const events = useMemo(() => {
    const out = [];
    (rows || []).forEach((r) => {
      const d = derive(r);
      const base = { recordId: r.id, mrn: r.mrn, order: d.orders || r.orderName, group: d.location || "—", stat: d.clinicPriority === "STAT" };
      let hist = [];
      try { hist = r.workHistory ? JSON.parse(r.workHistory) : []; } catch { hist = []; }
      // Track reviewed spells (oldest→newest). A user's FIRST "Reviewed" round in a spell is a real keep and counts
      // as work; a later "Reviewed" round by the same user in the same spell is a re-touch (0.25, scored via
      // reviewActs) — it must NOT inflate the Work done / process columns. A non-reviewed round ends the spell.
      let prevReviewed = false; const keptThisSpell = new Set();
      hist.forEach((h) => {
        const isRev = h.status === "Reviewed";
        let retouch = false;
        if (isRev) {
          if (!prevReviewed) keptThisSpell.clear();
          const by = h.by || "";
          if (keptThisSpell.has(by)) retouch = true; else keptThisSpell.add(by);
          prevReviewed = true;
        } else { prevReviewed = false; keptThisSpell.clear(); }
        out.push({
          ...base, user: h.by || "Unknown", date: mdyToDate(h.date), dateStr: h.date || "",
          stage: h.stage, status: h.status, statusDate: h.statusDate || h.date || "",
          reason: h.reason || "", remarks: h.remarks || "", issueType: h.issueType, issueReason: h.issueReason || "", retouch,
        });
      });
    });
    return out;
  }, [rows]);

  const allUsers = useMemo(() => Array.from(new Set(events.map((e) => e.user))).sort(), [events]);
  const allGroups = useMemo(() => Array.from(new Set(events.map((e) => e.group).filter((g) => g && g !== "—"))).sort(), [events]);

  // Week / month / custom → per-DATE rows; year → per-MONTH totals; today / yesterday / all-time → one total per user.
  const bucketMode = (period === "thisWeek" || period === "lastWeek" || period === "thisMonth" || period === "lastMonth" || period === "custom") ? "day"
    : (period === "thisYear" || period === "lastYear") ? "month" : null;
  const pad2 = (n) => String(n).padStart(2, "0");
  const bucketOf = (dt) => {
    if (!bucketMode || !dt) return "";
    return bucketMode === "day" ? `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}` : `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}`;
  };
  const bucketLabel = (key) => {
    if (!key) return "";
    const pp = key.split("-");
    if (bucketMode === "day") return `${pp[1]}/${pp[2]}/${pp[0]}`;
    return new Date(Number(pp[0]), Number(pp[1]) - 1, 1).toLocaleDateString([], { month: "short", year: "numeric" });
  };

  const report = useMemo(() => {
    const byUser = new Map();
    events.forEach((e) => {
      if (selUsers.size && !selUsers.has(e.user)) return;
      if (group && e.group !== group) return;
      if (!e.date && (rFrom || rTo)) return;
      if (e.date) {
        const ed = new Date(e.date.getFullYear(), e.date.getMonth(), e.date.getDate()).getTime();
        if (rFrom) { const f = new Date(rFrom.getFullYear(), rFrom.getMonth(), rFrom.getDate()).getTime(); if (ed < f) return; }
        if (rTo) { const t2 = new Date(rTo.getFullYear(), rTo.getMonth(), rTo.getDate()).getTime(); if (ed > t2) return; }
      }
      const bkt = bucketOf(e.date);
      const key = bucketMode ? bkt + "|" + e.user : e.user;
      if (!byUser.has(key)) { const nb = blank(); nb.__bucket = bkt; nb.__user = e.user; byUser.set(key, nb); }
      const b = byUser.get(key);
      // A reviewed re-touch is not new work — keep it out of the Work done (process) and STAT columns.
      if (!e.retouch) { const sk = STAGE_KEY[e.stage]; if (sk) b[sk] += 1; if (e.stat) b.stat += 1; }
      switch (e.status) {
        case "Approved": b.approved += 1; break;
        case "PA Not Required": b.paNotReq += 1; break;
        case "Denied": b.denied += 1; b.deniedL.push(e); break;
        case "Pending": b.pending += 1; break;
        case "Canceled": b.canceled += 1; b.canceledL.push(e); break;
        case "Withdraw": b.withdraw += 1; b.withdrawL.push(e); break;
        // Faxed / Submitted via call = submitted to the payor, awaiting a response → counted as Pending (no separate column).
        case "Faxed": case "Submitted via call": b.pending += 1; break;
        // Reviewed is counted from reviewActs below (1.0 for a new keep, 0.25 for a later re-touch) — not here.
        case "Reviewed": break;
        default: break;
      }
      if (e.issueType === "Client") { b.clientIssue += 1; b.clientL.push(e); }
      else if (e.issueType === "Patient") { b.patientIssue += 1; b.patientL.push(e); }
      else if (e.issueType === "Production") { b.productionIssue += 1; b.productionL.push(e); }
    });
    // Reviewed score: 1.0 for the day a record was newly kept in Reviewed, 0.25 for each later day it was re-touched.
    (reviewActs || []).forEach((a) => {
      if (selUsers.size && !selUsers.has(a.user)) return;
      if (group && a.group !== group) return;
      const dt = isoToDate(a.day);
      if (!dt) return;
      const ad = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()).getTime();
      if (rFrom) { const f = new Date(rFrom.getFullYear(), rFrom.getMonth(), rFrom.getDate()).getTime(); if (ad < f) return; }
      if (rTo) { const t2 = new Date(rTo.getFullYear(), rTo.getMonth(), rTo.getDate()).getTime(); if (ad > t2) return; }
      const bkt = bucketOf(dt);
      const key = bucketMode ? bkt + "|" + a.user : a.user;
      if (!byUser.has(key)) { const nb = blank(); nb.__bucket = bkt; nb.__user = a.user; byUser.set(key, nb); }
      byUser.get(key).reviewed += (Number(a.weight) || 0);
    });
    return Array.from(byUser.values()).map((b) => {
      const decided = b.approved + b.paNotReq + b.denied + b.pending + b.canceled + b.withdraw + b.reviewed;
      return { user: b.__user, bucket: b.__bucket, ...b, decided, approvalPct: decided ? (b.approved / decided) * 100 : 0 };
    }).sort((a, b) => bucketMode ? ((a.bucket < b.bucket ? -1 : a.bucket > b.bucket ? 1 : (b.decided - a.decided || a.user.localeCompare(b.user)))) : (b.decided - a.decided || a.user.localeCompare(b.user)));
  }, [events, reviewActs, selUsers, group, rFrom, rTo, bucketMode]);

  const totals = useMemo(() => {
    const t = blank(); let approved = 0, decided = 0;
    report.forEach((r) => {
      ["submissions", "endProcess", "modifications", "moreInfo", "stat", "approved", "paNotReq", "denied", "pending", "canceled", "withdraw", "reviewed", "clientIssue", "patientIssue", "productionIssue"].forEach((k) => { t[k] += r[k]; });
      ["deniedL", "canceledL", "withdrawL", "clientL", "patientL", "productionL"].forEach((k) => { t[k] = t[k].concat(r[k]); });
      approved += r.approved; decided += r.decided;
    });
    return { ...t, approvalPct: decided ? (approved / decided) * 100 : 0 };
  }, [report]);

  const toggleUser = (u) => setSelUsers((p) => { const n = new Set(p); n.has(u) ? n.delete(u) : n.add(u); return n; });
  const clearAll = () => { setSelUsers(new Set()); setGroup(""); setFrom(""); setTo(""); setPeriod(""); };
  const openDrill = (title, kind, items) => { if (items && items.length) setDrill({ title, kind, items }); };

  const exportExcel = () => {
    const esc = (v) => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const dateHead = bucketMode ? `<th style="background:#4f46e5;color:#fff;padding:6px 10px;border:1px solid #ccc">${esc(bucketMode === "day" ? "Date" : "Month")}</th>` : "";
    const head = dateHead + EXPORT_COLS.map(([l]) => `<th style="background:#4f46e5;color:#fff;padding:6px 10px;border:1px solid #ccc">${esc(l)}</th>`).join("");
    const body = report.map((r) => "<tr>" + (bucketMode ? `<td style="padding:5px 10px;border:1px solid #ddd">${esc(bucketLabel(r.bucket))}</td>` : "") + EXPORT_COLS.map(([, k]) => {
      const v = k === "approvalPct" ? r[k].toFixed(1) + "%" : k === "reviewed" ? fmtRev(r[k]) : r[k];
      return `<td style="padding:5px 10px;border:1px solid #ddd;text-align:${k === "user" ? "left" : "center"}">${esc(v)}</td>`;
    }).join("") + "</tr>").join("");
    const totalRow = "<tr>" + (bucketMode ? `<td style="padding:5px 10px;border:1px solid #ccc;font-weight:bold;background:#eef2ff"></td>` : "") + EXPORT_COLS.map(([, k], i) => {
      const v = i === 0 ? `All (${report.length})` : (k === "approvalPct" ? totals.approvalPct.toFixed(1) + "%" : k === "reviewed" ? fmtRev(totals[k]) : totals[k]);
      return `<td style="padding:5px 10px;border:1px solid #ccc;font-weight:bold;background:#eef2ff;text-align:${i === 0 ? "left" : "center"}">${esc(v)}</td>`;
    }).join("") + "</tr>";
    const html = `<html><head><meta charset="utf-8"></head><body><table style="border-collapse:collapse;font-family:Arial;font-size:12px"><thead><tr>${head}</tr></thead><tbody>${body}${totalRow}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const scope = selUsers.size === 0 ? "all-users" : selUsers.size === 1 ? [...selUsers][0].replace(/\s+/g, "-") : `${selUsers.size}-users`;
    a.href = url; a.download = `productivity-${scope}-${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  if (rows === null) return <div className="empty"><Spinner /></div>;

  const recordsById = {};
  (rows || []).forEach((r) => { recordsById[r.id] = r; });
  if (drill) return <ProductivityDrill title={drill.title} items={drill.items} recordsById={recordsById} onBack={() => setDrill(null)} />;

  const usersLabel = selUsers.size === 0 ? "All users" : `${selUsers.size} user${selUsers.size > 1 ? "s" : ""} selected`;

  const ClkCell = ({ n, cls, items, title }) => (
    n && items && items.length
      ? <td className={"prod-clk " + (cls || "")} onClick={() => openDrill(title, "status", items)}>{n}</td>
      : <td className={cls}>{n || "—"}</td>
  );
  const IssueCell = ({ n, items, title, first }) => (
    <td className={"prod-mod" + (first ? " prod-mod-first sec-issue" : "")}>
      {n && items && items.length
        ? <button type="button" className="prod-inner-clk" onClick={() => openDrill(title, "issue", items)}>{n}</button>
        : (n || "—")}
    </td>
  );

  return (
    <div className="prod">
      <div className="prod-filters">
        <div className="prod-field">
          <span className="prod-flabel">Users</span>
          <div className="prod-users">
            <button className="inp prod-users-btn" onClick={() => setUsersOpen((o) => !o)}>
              <span className="prod-users-txt"><Icon.users width="15" height="15" /> {usersLabel}</span> <Icon.chevron width="14" height="14" />
            </button>
            {usersOpen && (
              <>
                <div className="ctx-backdrop" onClick={() => setUsersOpen(false)} />
                <div className="prod-users-menu">
                  <label className="prod-user-row prod-user-all"><input type="checkbox" checked={selUsers.size === 0} onChange={() => setSelUsers(new Set())} /> All users</label>
                  <div className="prod-user-list">
                    {allUsers.map((u) => <label key={u} className="prod-user-row"><input type="checkbox" checked={selUsers.has(u)} onChange={() => toggleUser(u)} /> {u}</label>)}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="prod-field">
          <span className="prod-flabel">Group</span>
          <select className="inp" value={group} onChange={(e) => setGroup(e.target.value)}>
            <option value="">All groups</option>
            {(allGroups.length ? allGroups : ["LACN", "CCCMG"]).map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>

        <div className="prod-field">
          <span className="prod-flabel">Period</span>
          <select className="inp" value={period} onChange={(e) => { setPeriod(e.target.value); if (e.target.value !== "custom") { setFrom(""); setTo(""); } }}>
            <option value="">All time</option>
            {QUICKS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
            <option value="custom">Custom range…</option>
          </select>
        </div>

        <div className="prod-field"><span className="prod-flabel">From</span>
          <input className="inp" type="date" value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); setPeriod("custom"); }} /></div>
        <div className="prod-field"><span className="prod-flabel">To</span>
          <input className="inp" type="date" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPeriod("custom"); }} /></div>

        <button className="link-btn prod-clear" onClick={clearAll}>Clear</button>
        <button className="btn btn-ghost btn-sm" onClick={() => load()}
          title="Auto-refreshes every 10 seconds — click to refresh now"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#10b981", display: "inline-block" }} /> ↻ Refresh · Live every 10s
        </button>
        <button className="btn btn-primary prod-export" onClick={exportExcel} disabled={!report.length}>Export to Excel</button>
      </div>

      <div className="card prod-card"><div className="table-wrap">
        <table className="tbl prod-tbl">
          <thead>
            <tr>
              {bucketMode && <th rowSpan="2" className="prod-usercol">{bucketMode === "day" ? "Date" : "Month"}</th>}
              <th rowSpan="2" className="prod-usercol">User</th>
              <th colSpan="4" className="prod-grouphead sec-work">Work done</th>
              <th colSpan="8" className="prod-grouphead sec-status">Status</th>
              <th colSpan="3" className="prod-grouphead sec-issue">Modifications</th>
              <th rowSpan="2" className="prod-pct sec-pct">Approval %</th>
            </tr>
            <tr>
              <th className="sec-work">SUB</th><th>EP</th><th>MOD</th><th>MI</th>
              <th className="sec-status">STAT</th><th>Approved</th><th>PA N/R</th><th>Denied</th><th>Pending</th><th>Canceled</th><th>Withdraw</th><th>Reviewed</th>
              <th className="sec-issue">Client</th><th>Patient</th><th>Production</th>
            </tr>
          </thead>
          <tbody>
            {report.length > 1 && (
              <tr className="prod-total prod-total-top">
                {bucketMode && <td></td>}
                <td className="prod-usercol">All ({report.length})</td>
                <td className="sec-work">{totals.submissions}</td><td>{totals.endProcess}</td><td>{totals.modifications}</td><td>{totals.moreInfo}</td>
                <td className="sec-status">{totals.stat}</td><td className="pos">{totals.approved}</td><td className="pos">{totals.paNotReq}</td>
                <ClkCell n={totals.denied} cls="neg" items={totals.deniedL} title="All users — Denied" />
                <td>{totals.pending}</td>
                <ClkCell n={totals.canceled} items={totals.canceledL} title="All users — Canceled" />
                <ClkCell n={totals.withdraw} items={totals.withdrawL} title="All users — Withdrawn" />
                <td>{fmtRev(totals.reviewed)}</td>
                <IssueCell first n={totals.clientIssue} items={totals.clientL} title="All users — Client issues" />
                <IssueCell n={totals.patientIssue} items={totals.patientL} title="All users — Patient issues" />
                <IssueCell n={totals.productionIssue} items={totals.productionL} title="All users — Production issues" />
                <td className="prod-pct sec-pct"><b>{totals.approvalPct.toFixed(1)}%</b></td>
              </tr>
            )}
            {report.length === 0 ? (
              <tr><td colSpan={bucketMode ? 18 : 17} className="prod-empty">No activity for these filters.</td></tr>
            ) : report.map((r) => (
              <tr key={(r.bucket || "") + "|" + r.user}>
                {bucketMode && <td className="cell-sub">{bucketLabel(r.bucket)}</td>}
                <td className="prod-usercol"><b>{r.user}</b></td>
                <td className="sec-work">{r.submissions || "—"}</td><td>{r.endProcess || "—"}</td><td>{r.modifications || "—"}</td><td>{r.moreInfo || "—"}</td>
                <td className="sec-status">{r.stat || "—"}</td><td className="pos">{r.approved || "—"}</td><td className="pos">{r.paNotReq || "—"}</td>
                <ClkCell n={r.denied} cls="neg" items={r.deniedL} title={`${r.user} — Denied`} />
                <td>{r.pending || "—"}</td>
                <ClkCell n={r.canceled} items={r.canceledL} title={`${r.user} — Canceled`} />
                <ClkCell n={r.withdraw} items={r.withdrawL} title={`${r.user} — Withdrawn`} />
                <td>{fmtRev(r.reviewed)}</td>
                <IssueCell first n={r.clientIssue} items={r.clientL} title={`${r.user} — Client issues`} />
                <IssueCell n={r.patientIssue} items={r.patientL} title={`${r.user} — Patient issues`} />
                <IssueCell n={r.productionIssue} items={r.productionL} title={`${r.user} — Production issues`} />
                <td className="prod-pct sec-pct"><b>{r.approvalPct.toFixed(1)}%</b></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div></div>
      <p className="prod-note">Every round of work carries a status, so the Status columns add up to the work done in SUB + EP + MOD + MI. (Records submitted by fax or call — statuses Faxed / Submitted via call — count as Pending, submitted and awaiting a response.) <b>Reviewed</b> is shown as an effort score (1 each time a user keeps a record in Reviewed through submission or end-process work, plus 0.25 each time they later open an already-kept reviewed record and mark it — including when they approve/deny it that day), so it may not be a whole number. Approval % = Approved ÷ (Approved + Denied + Pending + Canceled + Withdraw + Reviewed) × 100. Click a <b>Denied / Canceled / Withdraw</b> or an <b>issue</b> number to see those records with their reasons and remarks.</p>

    </div>
  );
}